import { useMemo, useState, useEffect } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useLocation } from "react-router-dom";
import { Column } from "material-table";
import TableFilters from "components/TableFilters";
import useGetOfficeAppliancesBrand from "../hooks/useGetOfficeAppliancesBrand";
import useDeactivateOfficeAppliancesBrand from "../hooks/useDeactivateOfficeAppliancesBrand";
import {
  brandAddState,
  brandEditState,
  brandFilterText,
  brandRowForEdit,
  brandSelectedRows,
} from "../atoms/atoms";
import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import {
  IOfficeAppliancesBrand,
  IOfficeAppliancesBrandTableData,
} from "../type";
import { PERMISSIONS } from "modules/PermissionsConstants";
import { notificationFilterById } from "actions/Utilities/NotificationFilter";
import EditButton from "components/EditButton";
import AddButton from "components/AddButton";
import MaterialTableTS from "components/tables/MaterialTableTS";
import ConfirmBoxDelete from "components/ConfirmBoxDelete";
import OfficeAppliancesBrandForm from "../forms/OfficeAppliancesBrandForm";

export const OfficeAppliancesBrandTable = () => {
  const location = useLocation<{
    recordId?: string;
    moduleName?: string;
    subModuleName?: string;
  }>();

  const notifParams = location.state;

  const { data: brandData } = useGetOfficeAppliancesBrand();
  const bulkDelete = useDeactivateOfficeAppliancesBrand();

  const [actionAddState, setActionAddState] = useRecoilState(brandAddState);
  const [selectedRows, setSelectedRows] = useRecoilState(brandSelectedRows);
  const [actionEditState, setActionEditState] = useRecoilState(brandEditState);
  const [filterTextState, setFilterTextState] = useRecoilState(brandFilterText);
  const setEditRowData = useSetRecoilState(brandRowForEdit);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [tableData, setTableData] = useState<IOfficeAppliancesBrand[]>([]);
  const [tableFilteredData, setTableFilteredData] = useState<
    IOfficeAppliancesBrand[]
  >([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [recent, setRecent] = useState("");

  const addPermission = PERMISSIONS.permission_officeappliancebrand_add;
  const deletePermission = PERMISSIONS.permission_officeappliancebrand_delete;
  const editPermission = PERMISSIONS.permission_officeappliancebrand_edit;

  useEffect(() => {
    if (Array.isArray(brandData)) {
      setTableData(brandData.map((d) => ({ ...d })));
    } else {
      setTableData([]);
    }
  }, [brandData]);

  const isLoading = !brandData;

  useEffect(() => {
    if (
      notifParams?.recordId &&
      notifParams?.moduleName &&
      notifParams?.subModuleName
    ) {
      const action = "VIEW";

      notificationFilterById(
        notifParams.recordId,
        notifParams.moduleName,
        notifParams.subModuleName,
        action
      ).then((result: IOfficeAppliancesBrand[] | undefined) => {
        if (Array.isArray(result) && result.length > 0) {
          setTableFilteredData(result);
          setRecent(result[0].officeApplianceBrand1 ?? "");
        } else {
          setTableFilteredData([]);
          setRecent("");
        }
      });
    } else {
      setRecent("");
      setTableFilteredData([]);
    }
    setSelectedRows([]);
  }, [
    notifParams?.recordId,
    notifParams?.moduleName,
    notifParams?.subModuleName,
    setSelectedRows,
  ]);

  const filteredItems = useMemo(() => {
    const search = filterTextState.trim().toLowerCase();
    if (search === "") return tableData;

    return tableData.filter((item) =>
      item.officeApplianceBrand1?.toLowerCase().includes(search)
    );
  }, [tableData, filterTextState]);

  useEffect(() => {
    if (!recent) {
      setTableFilteredData(filteredItems);
    }
  }, [filterTextState, filteredItems, recent]);

  const handleAdd = () => {
    setActionAddState(true);
    setActionEditState(false);
    setEditRowData(null as any);
    setSelectedRows([]);
  };

  const handleSelectedRowsChange = (
    rows: IOfficeAppliancesBrandTableData[]
  ) => {
    const forDelete = rows
      .filter((x) => x.isActive && x.officeApplianceBrandId != null)
      .map((r) => {
        const { tableData, ...rest } = r;
        return rest;
      });

    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData: IOfficeAppliancesBrandTableData) => {
    delete (rowData as any).tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
    setActionAddState(false);
  };

  const handleDelete = async () => {
    const ids = selectedRows?.map(
      (row) => row.officeApplianceBrandId as number
    );

    if (ids?.length === 0) return;

    bulkDelete(ids);
    setSelectedRows([]);
    setRecent("");
    setOpenModalDelete(false);
  };

  const deleteMessage = `Are you sure you want to deactivate ${rowsCount} ${
    rowsCount > 1 ? "Brands" : "Brand"
  }?`;

  const matColumns: Column<object>[] = [
    {
      title: "Brand",
      field: "officeApplianceBrand1",
      customSort: (a: any, b: any) => {
        if (a.officeApplianceBrand1 && b.officeApplianceBrand1) {
          return a.officeApplianceBrand1
            .trim()
            .localeCompare(b.officeApplianceBrand1.trim());
        }
        if (a.officeApplianceBrand1) {
          return 0;
        }
        return -1;
      },
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a: any, b: any) => {
        if (a.createdBy && b.createdBy) {
          return a.createdBy.trim().localeCompare(b.createdBy.trim());
        }
        if (a.createdBy) {
          return 0;
        }
        return -1;
      },
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date",
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a: any, b: any) => {
        if (a.modifiedBy && b.modifiedBy) {
          return a.modifiedBy.trim().localeCompare(b.modifiedBy.trim());
        }
        if (a.modifiedBy) {
          return 0;
        }
        return -1;
      },
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData: any) => (rowData?.isActive ? "Active" : "Inactive"),
    },
    {
      title: "Action/s",
      align: "center",
      hidden: !userPermissions.includes(editPermission),
      sorting: false,
      render: (rowData: any) => {
        const typedRow = rowData as IOfficeAppliancesBrandTableData;

        return (
          <div className="flex items-center place-content-center">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows?.length > 0)
              }
              onClick={() => handleEdit(typedRow)}
            />
          </div>
        );
      },
    },
  ];

  let tableRows: IOfficeAppliancesBrand[] = [];

  if (isLoading) {
    tableRows = [];
  } else if (filterTextState === "" && recent === "") {
    tableRows = tableData;
  } else if (filterTextState === "") {
    tableRows = tableFilteredData;
  } else {
    tableRows = filteredItems;
  }

  return (
    <div className="bg-white p-4">
      {actionAddState || actionEditState ? (
        <OfficeAppliancesBrandForm setRecent={setRecent} />
      ) : (
        <>
          <div className="flex justify-between text-xs items-center w-full">
            <TableFilters
              defaultText={filterTextState}
              textInputName={"BrandName"}
              columnDetails={"Brand"}
              filterText={setFilterTextState}
              recentText={recent}
              setRecent={setRecent}
              disabledButton={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              disabledTextBox={
                selectedRows !== undefined && selectedRows.length > 0
              }
            />
            <div>
              {userPermissions.includes(addPermission) ? (
                <AddButton
                  buttonText="New Brand"
                  onClick={handleAdd}
                  disabled={
                    actionEditState ||
                    (selectedRows !== undefined && selectedRows.length > 0)
                  }
                />
              ) : null}
            </div>
          </div>
          <div>
            {userPermissions.includes(deletePermission) ? (
              <div className="text-secondary italic text-xs3 my-2">
                <p className="font-bold italic inline-block mr-1">Note:</p>
                <span>Tick the box to deactivate some data.</span>
              </div>
            ) : (
              <div className="mb-4" />
            )}
          </div>

          <div>
            <MaterialTableTS
              data={tableRows}
              showTitle={true}
              columns={matColumns}
              isLoading={isLoading}
              onSelectionChange={handleSelectedRowsChange}
              selectedRows={selectedRows}
              selection={
                !actionEditState &&
                !actionAddState &&
                userPermissions.includes(deletePermission)
              }
              setConfirmModalOpen={setOpenModalDelete}
            />
          </div>

          <ConfirmBoxDelete
            ModalTitle="Deactivate Brand"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => setOpenModalDelete(false)}
            onClickFunction={handleDelete}
            cancelButtonRef={undefined}
          />
        </>
      )}
    </div>
  );
};
export default OfficeAppliancesBrandTable;
